import { useCallback, useEffect, useRef, useState } from 'react';

import { EscanerQr } from '../componentes/EscanerQr';
import { Boton, Chip, Encabezado, ErrorCaja, Panel } from '../componentes/ui';
import { Iconos } from '../componentes/iconos';
import { ErrorApi, pedir } from '../lib/api';
import { usarPantallaEncendida } from '../lib/pantalla';

/**
 * El guardia lee el QR de una visita y el sistema le dice si pasa.
 *
 * ## Para qué existe
 *
 * Hasta ahora el único que sabía leer un código de invitación era el terminal
 * de la reja: una máquina con su lector por puerto serie. Un fraccionamiento
 * que no quiere terminal —o que todavía no lo tiene— se quedaba sin forma de
 * validar nada, aunque sus residentes ya pudieran generar códigos desde la app.
 * Esto cierra ese hueco con lo más barato que hay: una lectora USB conectada a
 * cualquier computadora.
 *
 * ## Por qué es una pantalla aparte y no un botón en la caseta
 *
 * La pantalla de caseta es para **decidir**: ver quién espera, mirar su foto,
 * liberar o rechazar. Esta es para **despachar**, y se usa de otra manera — sin
 * ratón, sin leer, mirando a la persona que tienes enfrente y no al monitor.
 * Mezclarlas obligaría a una de las dos a ceder, y la que cedería es esta, que
 * es la que tiene a alguien esperando en la ventanilla.
 *
 * ## La lectora escribe donde haya foco
 *
 * Una lectora USB es un teclado: teclea el código y manda Enter. No hay API que
 * consultar ni permiso que pedir, pero tiene una consecuencia que manda sobre
 * todo el diseño: **si el campo pierde el foco, el código se escribe en otra
 * parte** —o en ninguna— y el guardia ve que no pasa nada sin saber por qué.
 * De ahí que el campo se reenfoque solo, al cargar, al terminar cada lectura y
 * cada vez que alguien toca cualquier sitio de la pantalla.
 *
 * ## Y también con la cámara
 *
 * Una tablet o un teléfono salen más baratos que una lectora y un equipo donde
 * conectarla, así que la misma pantalla lee por cámara. Eso **exige contexto
 * seguro**: por HTTP plano el navegador no entrega cámara y el botón lo dice en
 * vez de fallar en silencio. Sirviendo el portal por HTTPS —como el VPS— un
 * navegador cualquiera basta, sin instalar nada.
 *
 * Mientras la cámara está abierta el campo **no** se reenfoca: en una tablet un
 * campo enfocado levanta el teclado en pantalla y se come media vista justo
 * donde está la imagen que hay que apuntar.
 */

/** Lo que se le enseña al guardia después de leer un código. */
interface Resultado {
  /** Para listar los últimos sin que React se queje ni se mezclen dos iguales. */
  id: number;
  veredicto: 'pasa' | 'no-pasa' | 'revisar';
  titulo: string;
  detalle: string | null;
  hora: string;
}

/** Cuántas lecturas recientes se recuerdan. */
const RECIENTES = 6;

/** El acceso elegido en la caseta. La misma llave, para no preguntarlo dos veces. */
const CLAVE_PUNTO = 'kodica.puntoAcceso';

interface InvitacionValidada {
  unidad?: string | null;
  nombre?: string | null;
  anfitrion?: string | null;
}

interface AccesoCreado {
  accesoId: string;
  estado: 'DENTRO' | 'PENDIENTE_AUTORIZACION';
}

function ahora(): string {
  return new Date().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
}

export function ValidarPagina() {
  // La computadora de la caseta no puede dormirse: despertarla son los segundos
  // que no hay con alguien esperando en la ventanilla.
  usarPantallaEncendida(true);

  const [codigo, setCodigo] = useState('');
  const [trabajando, setTrabajando] = useState(false);
  const [ultimo, setUltimo] = useState<Resultado | null>(null);
  const [recientes, setRecientes] = useState<Resultado[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [camara, setCamara] = useState(false);
  const campo = useRef<HTMLInputElement>(null);
  const contador = useRef(0);

  /**
   * Devuelve el foco al campo.
   *
   * Se llama más veces de las que parecen necesarias, y es a propósito: el
   * precio de un foco perdido es un código que se escribe en el vacío delante
   * de alguien que está esperando.
   */
  const enfocar = useCallback(() => {
    if (camara) return;
    campo.current?.focus();
  }, [camara]);

  useEffect(() => {
    enfocar();
    // Cualquier toque en la pantalla devuelve el foco. Pasa de verdad: el
    // guardia señala algo en el monitor y la siguiente lectura se pierde.
    document.addEventListener('click', enfocar);
    return () => document.removeEventListener('click', enfocar);
  }, [enfocar]);

  function anotar(r: Omit<Resultado, 'id' | 'hora'>) {
    const completo: Resultado = { ...r, id: ++contador.current, hora: ahora() };
    setUltimo(completo);
    setRecientes((prev) => [completo, ...prev].slice(0, RECIENTES));
  }

  async function leer(token: string) {
    setTrabajando(true);
    setError(null);
    try {
      // **Dos llamadas, y las dos hacen falta.** La primera devuelve de quién es
      // la visita, que es lo que el guardia necesita ver. La segunda deja el
      // paso anotado en la bitácora — y revalida el token por su cuenta, sin
      // fiarse de la anterior, así que una invitación no se puede usar dos
      // veces ni después de expirar.
      const inv = await pedir<InvitacionValidada>('/kiosko/validar-qr', {
        metodo: 'POST',
        cuerpo: { token },
      });

      const punto = localStorage.getItem(CLAVE_PUNTO) ?? '';
      const acceso = await pedir<AccesoCreado>('/kiosko/accesos', {
        metodo: 'POST',
        cuerpo: { token, ...(punto ? { puntoAccesoId: punto } : {}) },
      });

      const quien = inv.nombre?.trim() || 'Visita sin nombre';
      const donde = inv.unidad?.trim();

      if (acceso.estado === 'DENTRO') {
        anotar({
          veredicto: 'pasa',
          titulo: quien,
          detalle: donde ? `Vivienda ${donde}` : null,
        });
      } else {
        // No deberia pasar con una invitacion válida —el residente ya autorizó
        // al invitar—, pero si pasa, el guardia tiene que saber que esto no se
        // resuelve aquí sino en la pantalla de caseta.
        anotar({
          veredicto: 'revisar',
          titulo: quien,
          detalle: 'Queda pendiente de autorizar en la caseta',
        });
      }
    } catch (e) {
      if (e instanceof ErrorApi) {
        // El servidor redacta estos mensajes para que los lea una persona
        // —«la invitación expiró», «ya se usó»—, así que se enseñan tal cual.
        anotar({ veredicto: 'no-pasa', titulo: 'No pasa', detalle: e.message });
      } else {
        setError('No se pudo consultar el servidor. Revisa la conexión.');
      }
    } finally {
      setTrabajando(false);
      setCodigo('');
      enfocar();
    }
  }

  /**
   * Un código leído por la cámara.
   *
   * El escáner se detiene solo al encontrar uno, así que aquí se cierra y el
   * guardia decide cuándo abrir el siguiente. Reabrirlo solo seria mas rapido
   * pero dejaria la camara encendida sobre el resultado que acaba de salir, que
   * es justo lo que hay que leer.
   */
  function alLeerCamara(texto: string) {
    setCamara(false);
    const token = texto.trim();
    if (!token || trabajando) return;
    void leer(token);
  }

  function alEnviar(e: React.FormEvent) {
    e.preventDefault();
    const token = codigo.trim();
    // Una lectora puede disparar dos veces el mismo código si el visitante deja
    // el papel delante del cristal. Mientras hay una lectura en curso, la
    // siguiente se ignora.
    if (!token || trabajando) return;
    void leer(token);
  }

  return (
    <>
      <Encabezado titulo="Leer código de visita" />

      <div className="mx-auto w-full max-w-3xl px-4 pb-10">
        {error && <ErrorCaja>{error}</ErrorCaja>}

        {camara ? (
          <div className="mt-2">
            <EscanerQr
              titulo="Apunta al código de la visita"
              ayuda="El código que el residente le mandó por mensaje."
              onLeido={alLeerCamara}
              onCerrar={() => setCamara(false)}
            />
          </div>
        ) : (
          <div className="mt-2 flex justify-end">
            <Boton variante="contorno" icono={Iconos.buscar} onClick={() => setCamara(true)}>
              Leer con la cámara
            </Boton>
          </div>
        )}

        <form onSubmit={alEnviar} className="mt-2">
          <label className="text-label-sm text-on-surface-variant" htmlFor="codigo">
            Acerca el código a la lectora
          </label>
          <input
            id="codigo"
            ref={campo}
            value={codigo}
            onChange={(e) => setCodigo(e.target.value)}
            onBlur={() => window.setTimeout(enfocar, 0)}
            autoComplete="off"
            autoFocus
            placeholder="Esperando lectura…"
            className="mt-1 w-full rounded-xl border-2 border-outline-variant bg-surface-container-lowest px-4 py-4 text-title-md text-on-surface outline-none focus:border-primary"
          />
        </form>

        <Resultado ultimo={ultimo} trabajando={trabajando} />

        {recientes.length > 1 && (
          <section className="mt-8">
            <h2 className="text-label-sm mb-2 text-on-surface-variant">Lecturas recientes</h2>
            <Panel>
              <ul className="divide-y divide-outline-variant">
                {recientes.slice(1).map((r) => (
                  <li key={r.id} className="flex items-center justify-between gap-3 px-4 py-2">
                    <span className="text-body-md text-on-surface">
                      {r.titulo}
                      {r.detalle ? ` · ${r.detalle}` : ''}
                    </span>
                    <span className="flex shrink-0 items-center gap-2">
                      <Chip tono={r.veredicto === 'pasa' ? 'ok' : 'malo'}>
                        {r.veredicto === 'pasa' ? 'Pasó' : 'No pasó'}
                      </Chip>
                      <span className="text-label-sm tabular-nums text-on-surface-variant">
                        {r.hora}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </Panel>
          </section>
        )}
      </div>
    </>
  );
}

/**
 * El veredicto, en grande.
 *
 * Quien lee esto no está sentado mirando el monitor: está de pie, con una
 * persona enfrente y a veces con un coche detrás. Tiene que resolverse de un
 * vistazo y desde medio metro, así que manda el color y el tamaño — el detalle
 * es para cuando algo no cuadra y hay que explicarlo.
 */
function Resultado({ ultimo, trabajando }: { ultimo: Resultado | null; trabajando: boolean }) {
  if (trabajando) {
    return <p className="mt-8 text-center text-title-md text-on-surface-variant">Consultando…</p>;
  }

  if (!ultimo) {
    return (
      <p className="mt-8 text-center text-body-md text-on-surface-variant">
        El resultado de cada lectura aparece aquí.
      </p>
    );
  }

  // Los tonos salen del sistema del proyecto, no de colores sueltos: así este
  // cartel dice «bien» y «mal» con los mismos colores que el resto del portal,
  // y sigue al tema cuando el tema cambie.
  const tono =
    ultimo.veredicto === 'pasa'
      ? 'border-success bg-success-container text-success'
      : ultimo.veredicto === 'revisar'
        ? 'border-tertiary bg-tertiary-container/20 text-tertiary'
        : 'border-error bg-error-container text-on-error-container';

  const encabezado =
    ultimo.veredicto === 'pasa' ? 'PASA' : ultimo.veredicto === 'revisar' ? 'REVISAR' : 'NO PASA';

  return (
    <div className={`mt-8 rounded-2xl border-2 px-6 py-8 text-center ${tono}`} role="status">
      <p className="text-5xl font-bold tracking-tight">{encabezado}</p>
      <p className="mt-3 text-2xl font-semibold">{ultimo.titulo}</p>
      {ultimo.detalle && <p className="mt-1 text-lg">{ultimo.detalle}</p>}
    </div>
  );
}
